import { memo } from 'react'

import { LucideIcon } from '@/components/ui/Icons'
import { Spinner } from '@/components/ui/Loading'

interface MenuItemProps {
  icon: string // Lucide icon name
  label: string
  isActive: boolean
  onClick: () => void
  showBadge?: boolean
  badgeCount?: number
  isLoading?: boolean
}

export default memo(function MenuItem({
  icon,
  label,
  isActive,
  onClick,
  showBadge = false,
  badgeCount,
  isLoading = false,
}: MenuItemProps) {
  // Disable clicking if already on this page or loading
  const isDisabled = isActive || isLoading

  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm font-medium transition-colors duration-150 relative touch-manipulation ${
        isActive
          ? 'bg-primary/15 text-primary border-l-2 border-primary cursor-default'
          : isLoading
            ? 'text-foreground cursor-wait opacity-70'
            : 'text-foreground hover:bg-muted hover:text-primary active:bg-muted/80 cursor-pointer'
      }`}
    >
      <div className="relative">
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <>
            <LucideIcon name={icon} className="w-4 h-4" />
            {showBadge && (
              <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-destructive rounded-full" />
            )}
          </>
        )}
      </div>
      {label}
      {badgeCount !== undefined && badgeCount > 0 && (
        <span className="ml-auto min-w-[18px] h-4 px-1 flex items-center justify-center text-[10px] font-bold bg-destructive text-white rounded-full">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </button>
  )
})
